import React from 'react';

import { Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';

import { CombinedStatisticsKey } from 'src/features/statistic/types';
import { allColorCategoryMapping, statisticsTypeMapping } from 'src/features/statistic/utils/constants';
import { SectionBlockSubtitle } from 'src/registry/components/blocks/SectionBlock';
import { useIntl } from 'react-intl';
import { Skeleton } from 'src/registry/ui/Skeleton';
import colors from 'tailwindcss/colors';

type Props = {
    dataKey: string;
    data?: { [key: string]: string | number }[];
    isLoading: boolean;
    title: string;
    value?: number;
};

export default function HalfPieChart({ data, dataKey, isLoading, title, value }: Props) {
    const { formatMessage } = useIntl();
    const [index, setIndex] = React.useState<null | number>(null);

    const fallbackData = [{ value: 1 }];
    const hasData = data && data.length > 0;

    return (
        <div className="relative flex h-full w-full items-center justify-center">
            <ResponsiveContainer className="absolute z-10 aspect-square min-h-48" width="100%" height="100%">
                <PieChart>
                    <Pie
                        startAngle={180}
                        endAngle={0}
                        data={hasData ? data : fallbackData}
                        dataKey={dataKey}
                        cx="50%"
                        cy="75%"
                        innerRadius="70%"
                        outerRadius="100%"
                        isAnimationActive
                        shape={(props) => {
                            const isEmpty = !hasData;
                            return (
                                <Sector
                                    {...props}
                                    className="hover:opacity-75"
                                    fill={
                                        isEmpty
                                            ? colors.gray[100]
                                            : allColorCategoryMapping[
                                                  data?.[props.index]?.title as CombinedStatisticsKey
                                              ]
                                    }
                                    onMouseEnter={() => setIndex(!isEmpty ? props.index : null)}
                                    onMouseOut={() => setIndex(null)}
                                />
                            );
                        }}
                    />
                </PieChart>
            </ResponsiveContainer>

            <div className="absolute inset-x-0 top-28 z-0">
                <div className="flex flex-col items-center gap-2">
                    {isLoading ? (
                        <Skeleton className="h-8 w-27" />
                    ) : (
                        <p className="text-sm">
                            <span className="text-lg font-semibold md:text-xl xl:text-2xl">
                                {index !== null && data ? data[index]?.value : value}
                            </span>
                        </p>
                    )}
                    <SectionBlockSubtitle className="text-center text-sm font-medium">
                        {data && index !== null
                            ? formatMessage(
                                  statisticsTypeMapping[data[index]?.title] ?? {
                                      id: 'unknown',
                                      defaultMessage: title,
                                  },
                              )
                            : title}
                    </SectionBlockSubtitle>
                </div>
            </div>
        </div>
    );
}
